import { DateTime } from 'luxon';
import {
  array,
  date,
  number,
  object,
  SchemaOf,
  string,
  StringSchema,
  MixedSchema,
  mixed,
  BooleanSchema
} from 'yup';
import messages from 'locales/en-US';
import {
  Label,
  LabelType,
  LabelValueAcceptedBy,
  LabelValueSource,
  TimeSignalClassification
} from 'graphql/types';
import {
  CustomLabel,
  TimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import { isNoteNonEmpty, validateDuration } from './utils';

export enum NOTE_FIELDS {
  NOTE_ID = 'id',
  NOTE = 'note',
  START_TIME = 'startTime',
  END_TIME = 'endTime',
  DURATION = 'duration',
  TIMESHEET_LABELS = 'timesheetLabels',
  LABEL_VAL_INF_ID = 'labelValuesInferenceId',
  CUSTOM_LABELS = 'customLabels',
  DATE = 'date',
  CLASSIFICATION = 'classification'
}

export type TimesheetLabelFields = Record<string, TimesheetLabel>[];

// This is the format of the data after being transformed by the custom handleSubmit in useNoteForm.
export type NoteData = {
  id?: string;
  note?: string | null;
  startTime?: Date | null;
  endTime?: Date | null;
  duration?: number;
  timesheetLabels?: Label[];
  customLabels?: CustomLabel[];
  date?: Date | null;
  classification?: TimeSignalClassification | null;
  labelValuesInferenceId?: string | null;
};

// This is the format React Hook Forms stores the data in and the yup resolver receives for validation.
export type NoteFormData = {
  id?: string;
  note?: string | null;
  startTime?: Date | null;
  endTime?: Date | null;
  duration?: number;
  timesheetLabels?: TimesheetLabelFields;
  labelValuesInferenceId?: string | null;
  customLabels?: CustomLabel[];
  date?: Date | null;
  classification?: TimeSignalClassification | null;
};

export const COMMENT_MAX_CHAR_LIMIT = 300;
export const NOTE_MAX_CHAR_LIMIT = 2000;

export const noteSchema = (
  isDurationIndipendant?: boolean
): SchemaOf<NoteFormData> =>
  object()
    .shape(
      {
        id: string(),
        note: string().max(NOTE_MAX_CHAR_LIMIT, 'note.error.maxCommentLength'),
        startTime: date()
          .notRequired()
          .nullable()
          .when('endTime', {
            is: (val: Date | null) => Boolean(val),
            then: date().nullable().required('note.error.startTimeRequired')
          }),
        endTime: date()
          .notRequired()
          .nullable()
          .when('startTime', {
            is: (val: Date | null) => Boolean(val),
            then: date()
              .nullable()
              .required('note.error.endTimeRequired')
              .when(['startTime', 'endTime'], {
                is: (startTime: Date, endTime: Date) => startTime > endTime,
                then: date()
                  .nullable()
                  .test(
                    'startTime_endTime_validation',
                    'note.error.endTimeShouldBeGreaterThanStartTime',
                    () => false
                  ),
                otherwise: date()
                  .nullable()
                  .when(['startTime', 'endTime'], {
                    is: (startTime: Date, endTime: Date) => {
                      const start = DateTime.fromJSDate(startTime);
                      const end = DateTime.fromJSDate(endTime);
                      return start.toISO() === end.toISO();
                    },
                    then: date()
                      .nullable()
                      .test(
                        'startTime_endTime_validation',
                        'note.error.startAndEndTimeShouldNotBeEqual',
                        () => false
                      )
                  })
              })
          }),
        duration: number()
          .notRequired()
          .min(0)
          .max(86400)
          .when('startTime', {
            is: (val: Date | null) => Boolean(val),
            then: number().required('note.error.durationRequired')
          })
          .when('endTime', {
            is: (val: Date | null) => Boolean(val),
            then: number().required('note.error.durationRequired')
          })
          .when(['startTime', 'endTime'], {
            is: (startTime: Date, endTime: Date) =>
              isDurationIndipendant && Boolean(startTime) && Boolean(endTime),
            then: number()
              .required()
              .when(['startTime', 'endTime', 'duration'], {
                is: (startTime: Date, endTime: Date, duration: number) => {
                  const start = DateTime.fromJSDate(startTime);
                  const end = DateTime.fromJSDate(endTime);
                  const isDurationValid = validateDuration(
                    start,
                    end,
                    duration
                  );
                  return isDurationValid;
                },
                then: number()
                  .required()
                  .test(
                    'duration_validation',
                    'note.error.durationShouldBeInStartTimeEndTimeRange',
                    () => false
                  )
              })
          }),
        timesheetLabels: array()
          .notRequired()
          .of(
            object().shape({
              // Using the base constructors is a hack required to generate the correct types.
              // Appears to be related to an open issue with yup: https://github.com/jquense/yup/issues/1247.
              label: object().shape({
                id: new StringSchema<string>().required(),
                type: new MixedSchema<LabelType>()
                  .oneOf(Object.values(LabelType))
                  .required(),
                name: new StringSchema<string>().required(),
                defaultValue: object()
                  .notRequired()
                  .nullable()
                  .shape({
                    idValue: string().notRequired().nullable(),
                    stringValue: string().notRequired().nullable(),
                    numericValue: number().notRequired().nullable()
                  })
                  .default(undefined),
                value: object().when('type', (type: LabelType, schema) => {
                  if (type === LabelType.Comment) {
                    return schema
                      .notRequired()
                      .nullable()
                      .shape({
                        idValue: string().notRequired().nullable(),
                        stringValue: string()
                          .notRequired()
                          .nullable()
                          .max(COMMENT_MAX_CHAR_LIMIT),
                        numericValue: number().notRequired().nullable()
                      });
                  }

                  return schema.notRequired().nullable().shape({
                    idValue: string().notRequired().nullable(),
                    stringValue: string().notRequired().nullable(),
                    numericValue: number().notRequired().nullable()
                  });
                }),
                valueSource: new StringSchema<LabelValueSource>().notRequired(),
                thresholdMet: new BooleanSchema().notRequired(),
                acceptedBy: new StringSchema<LabelValueAcceptedBy>()
                  .notRequired()
                  .nullable(),
                suggestedValues: array()
                  .notRequired()
                  .nullable()
                  .of(
                    object().notRequired().nullable().shape({
                      idValue: string().notRequired().nullable(),
                      stringValue: string().notRequired().nullable(),
                      numericValue: number().notRequired().nullable()
                    })
                  )
              })
            })
          ),
        customLabels: array()
          .notRequired()
          .nullable()
          .of(
            object().shape({
              id: new StringSchema<string>().required(),
              type: new MixedSchema<LabelType>()
                .oneOf(Object.values(LabelType))
                .required(),
              name: new StringSchema<string>().required(),
              value: object().when('type', (type: LabelType, schema) => {
                return schema.notRequired().nullable().shape({
                  idValue: string().notRequired().nullable(),
                  stringValue: string().notRequired().nullable(),
                  numericValue: number().notRequired().nullable()
                });
              }),
              valueSource: new StringSchema<LabelValueSource>().notRequired(),
              acceptedBy: new StringSchema<LabelValueAcceptedBy>()
                .notRequired()
                .nullable(),
              suggestedValues: array()
                .notRequired()
                .nullable()
                .of(
                  object().notRequired().nullable().shape({
                    idValue: string().notRequired().nullable(),
                    stringValue: string().notRequired().nullable(),
                    numericValue: number().notRequired().nullable()
                  })
                )
            })
          ),
        date: date().notRequired().nullable(),
        classification: mixed<TimeSignalClassification>()
          .notRequired()
          .nullable(),
        labelValuesInferenceId: string().notRequired().nullable()
      },
      [['startTime', 'endTime']]
    )
    .test(
      'empty_note_validation',
      messages['note.error.emptyNote'],
      (value: NoteFormData) => {
        return isNoteNonEmpty(value);
      }
    );
